import Avatar, { type AvatarColor } from './Avatar';

interface Pessoa {
  nome: string;
  iniciais?: string;
  cor?: AvatarColor;
}

interface AvatarStackProps {
  pessoas: Pessoa[];
  /** Máximo de avatares visíveis antes de mostrar "+N". Default 4 */
  max?: number;
}

/**
 * Pilha de avatares sobrepostos com indicador "+N" para overflow.
 *
 * @example
 * <AvatarStack
 *   pessoas={[
 *     { nome: 'Marcos Filho', cor: 'brand' },
 *     { nome: 'Ana Silva', cor: 'tech' },
 *     { nome: 'Carlos Souza', cor: 'legal' },
 *   ]}
 *   max={4}
 * />
 */
export default function AvatarStack({ pessoas, max = 4 }: AvatarStackProps) {
  const visiveis = pessoas.slice(0, max);
  const overflow = pessoas.length - max;

  return (
    <span className="inline-flex">
      {visiveis.map((p, i) => (
        <Avatar
          key={`${p.nome}-${i}`}
          nome={p.nome}
          iniciais={p.iniciais}
          cor={p.cor ?? 'brand'}
          size="sm"
          stacked
        />
      ))}
      {overflow > 0 && (
        <span
          className="inline-flex items-center justify-center -ml-1.5 size-[22px] rounded-full border-2 border-[var(--bg-card)] bg-[var(--gray-200)] text-[var(--fg-muted)] text-[10px] font-semibold"
          title={`+${overflow} ${overflow === 1 ? 'pessoa' : 'pessoas'}`}
          aria-label={`Mais ${overflow} ${overflow === 1 ? 'pessoa' : 'pessoas'}`}
        >
          +{overflow}
        </span>
      )}
    </span>
  );
}
